/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { useQueries, useQueryClient } from '@tanstack/react-query';
import isEmpty from 'lodash/isEmpty';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
    getMonthlyAvailability,
    MONTHLY_AVAILABILITY_KEY,
    monthlyAvailabilityKey,
} from '../api/availability/get-monthly-availability';
import {
    FETCH_MONTHLY_AVAILABILITY_SUCCESS,
    PAUSE_LOADING,
    SET_RATE_LIMITED_MONTHS,
} from '../constants';
import { MAX_RETRY_COUNT } from '../constants/campground';
import { getAvailabilityMonthsToFetch } from '../utils/availabilityMonths';

const STALE_TIME_MS = 60_000;
const GC_TIME_MS = 5 * 60_000;

/**
 * Fetches the months of availability the grid needs and emits the same
 * FETCH_MONTHLY_AVAILABILITY_SUCCESS the fetch epic used to, leaving the downstream
 * transform epics untouched.
 *
 * Mount once per page, above the grid - the campsite list and map tabs read the
 * resulting processedAvailabilitiesMap even when the grid itself is unmounted.
 */
export function useMonthlyAvailability({
    campgroundId,
    isInternal = false,
    isModify = false,
}: {
    campgroundId?: string;
    isInternal?: boolean;
    isModify?: boolean;
}) {
    const dispatch = useDispatch();
    const queryClient = useQueryClient();

    const gridStartDate = useSelector<any, string | null>(
        (state) => state.availabilityGrid?.gridStartDate ?? null
    );
    const gridEndDate = useSelector<any, string | null>(
        (state) => state.availabilityGrid?.gridEndDate ?? null
    );
    const isLoading = useSelector<any, boolean>(
        (state) => state.availabilityGrid?.isLoading ?? false
    );
    const refreshCount = useSelector<any, number>(
        (state) => state.availabilityGrid?.availabilityRefreshCount ?? 0
    );

    const months = useMemo(
        () =>
            getAvailabilityMonthsToFetch({
                startDate: gridStartDate,
                endDate: gridEndDate,
                isInternal,
            }),
        [gridStartDate, gridEndDate, isInternal]
    );

    const isQueryEnabled = !!campgroundId;

    const results = useQueries({
        queries: months.map((month) => ({
            queryKey: monthlyAvailabilityKey(campgroundId!, month, isInternal),
            queryFn: ({ signal }: { signal: AbortSignal }) =>
                getMonthlyAvailability({
                    campgroundId: campgroundId!,
                    month,
                    isInternal,
                    signal,
                }),
            enabled: isQueryEnabled,
            staleTime: STALE_TIME_MS,
            gcTime: GC_TIME_MS,
            retry: (failureCount: number) => failureCount < MAX_RETRY_COUNT,
            retryDelay: (attempt: number) => Math.min(1000 * 2 ** attempt, 30_000),
        })),
    });

    /* consolidateCampgroundAvailability branches on receiving 1 vs 2 months in a single
    payload, so all in-range months must be dispatched together rather than as they land. */
    const allSettled =
        months.length > 0 &&
        results.every((r) => !r.isFetching && (r.isSuccess || r.isError));

    // useQueries returns a fresh array identity each render, so key the effect on contents.
    const settledSignature = allSettled
        ? results
              .map((r, i) => `${months[i]}:${r.dataUpdatedAt}:${r.errorUpdatedAt}`)
              .join('|')
        : '';

    const refreshAvailability = useCallback(() => {
        return queryClient.invalidateQueries({
            queryKey: [MONTHLY_AVAILABILITY_KEY, campgroundId],
        });
    }, [queryClient, campgroundId]);

    /* Every `shouldRefresh: true` caller bumps availabilityRefreshCount, which is how they
    bypass staleTime without needing a reference to this hook. */
    const lastRefreshCountRef = useRef(refreshCount);

    useEffect(() => {
        if (refreshCount === lastRefreshCountRef.current) {
            return;
        }
        lastRefreshCountRef.current = refreshCount;

        if (isQueryEnabled) {
            refreshAvailability();
        }
    }, [refreshCount, isQueryEnabled, refreshAvailability]);

    const lastDispatchedRef = useRef<string>('');
    const lastDispatchedRefreshCountRef = useRef(refreshCount);

    useEffect(() => {
        if (!isQueryEnabled) {
            return;
        }
        if (months.length === 0) {
            if (isLoading) {
                dispatch({ type: PAUSE_LOADING });
            }
            return;
        }
        if (!settledSignature) {
            return;
        }
        if (lastDispatchedRef.current === settledSignature) {
            /* A non-refresh request may be served entirely from cache. A refresh must wait
            for invalidation to replace the cached signature before loading can stop. */
            if (isLoading && lastDispatchedRefreshCountRef.current === refreshCount) {
                dispatch({ type: PAUSE_LOADING });
            }
            return;
        }
        lastDispatchedRef.current = settledSignature;
        lastDispatchedRefreshCountRef.current = refreshCount;

        const monthData: Record<string, unknown> = {};
        const rateLimitedMonths: Record<string, boolean> = {};

        results.forEach((result, index) => {
            const month = months[index];

            if (result.isSuccess && result.data?.campsites) {
                monthData[month] = result.data.campsites;
                return;
            }

            if (result.isError && (result.error as any)?.response?.status === 429) {
                /* Keyed by month rather than an array: the reducer merges this into an object
                and RateLimitModal's retry iterates the keys as start dates. */
                rateLimitedMonths[month] = true;
            }
        });

        if (!isEmpty(monthData)) {
            dispatch({
                type: FETCH_MONTHLY_AVAILABILITY_SUCCESS,
                monthData,
                isModify,
            });
        } else {
            dispatch({ type: PAUSE_LOADING });
        }

        if (!isEmpty(rateLimitedMonths)) {
            dispatch({
                type: SET_RATE_LIMITED_MONTHS,
                months: rateLimitedMonths,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        settledSignature,
        isQueryEnabled,
        isModify,
        isLoading,
        refreshCount,
        dispatch,
        months.length,
    ]);

    return {
        months,
        isFetching: results.some((r) => r.isFetching),
        refreshAvailability,
    };
}
